import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload, Server, History, Languages, Volume2, Loader2, Columns, FileCode } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/core/components/ui/popover';
import { Checkbox } from '@/core/components/ui/checkbox';
import { ApplicationTypeSelect } from './components/atoms/ApplicationTypeSelect';
import { TranslationSearchBar } from './components/molecules/TranslationSearchBar';
import { TranslationGrid } from './components/organisms/TranslationGrid';
import { TranslationBlade } from './components/organisms/TranslationBlade';
import { useTranslationStore } from './stores/translation-store';
import { useLanguageStore } from './stores/language-store';
import { useApplicationLanguagesStore } from './stores/application-languages-store';
import { ApplicationType, TranslationKey, Language, UpdateTranslationRequest } from './types/translation-types';
import { supportsSounds } from './types/sound-management-types';
import { toast } from 'sonner';

const MISSING_ENGLISH_MESSAGE = 'Add an English value first - this key has no English translation';

// The backend rejects any update without a non-empty 'en' value (keys imported without English exist).
// Whitespace counts as a value - a single space is a valid translation, so no trim here.
function hasEnglishValue(translation: TranslationKey): boolean {
  return translation.values.some(v => v.languageCode.toLowerCase() === 'en' && v.value !== '');
}

/**
 * Build a full update payload from an existing translation.
 * The backend requires all values (incl. English) and always overwrites the description,
 * so partial payloads either fail or wipe data.
 *
 * Known limitation: the payload is built from the values loaded in the grid, and the backend
 * has no concurrency check (last write wins). If another user changed other languages or the
 * description after this grid loaded, an inline edit here overwrites them with the stale values.
 */
function buildFullUpdate(
  translation: TranslationKey,
  overrides: { description?: string; value?: { languageCode: string; value: string } }
): UpdateTranslationRequest {
  const values = translation.values
    .filter(v => v.languageCode !== overrides.value?.languageCode)
    .map(v => ({ languageCode: v.languageCode, value: v.value }));
  if (overrides.value) values.push(overrides.value);

  return {
    description: overrides.description !== undefined ? overrides.description : translation.description,
    values,
  };
}

export function Translations() {
  const navigate = useNavigate();
  const {
    translations,
    totalCount,
    currentPage,
    pageSize,
    selectedAppType,
    searchQuery,
    isLoading,
    isSubmitting,
    error,
    isBackgroundLoading,
    isAllDataLoaded,
    isSearchingServer,
    fetchTranslations,
    fetchAllTranslations,
    searchLocally,
    updateTranslation,
    deleteTranslation,
    setSelectedAppType,
    setCurrentPage,
  } = useTranslationStore();

  const { languages: globalLanguages, fetchLanguages } = useLanguageStore();
  const {
    applicationLanguages,
    fetchApplicationLanguages,
    isLoading: isLoadingAppLanguages,
  } = useApplicationLanguagesStore();

  const [isBladeOpen, setIsBladeOpen] = useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState<TranslationKey | null>(null);

  // Column visibility state with localStorage persistence
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    const stored = localStorage.getItem('translation-grid-hidden-columns');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  const handleToggleColumn = useCallback((columnId: string) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      localStorage.setItem('translation-grid-hidden-columns', JSON.stringify([...next]));
      return next;
    });
  }, []);

  const handleShowAllColumns = useCallback(() => {
    setHiddenColumns(new Set());
    localStorage.setItem('translation-grid-hidden-columns', JSON.stringify([]));
  }, []);

  // Fetch global languages on mount (fallback)
  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  // Fetch application-specific languages when app type changes
  useEffect(() => {
    if (selectedAppType) {
      fetchApplicationLanguages(selectedAppType);
    }
  }, [selectedAppType, fetchApplicationLanguages]);

  // Convert application languages to Language format for grid
  // Use app-specific languages if available, otherwise fall back to global
  const languages: Language[] = useMemo(() => {
    if (selectedAppType && applicationLanguages.length > 0) {
      return applicationLanguages.map((appLang) => ({
        languageId: appLang.languageId,
        languageCode: appLang.languageCode,
        languageName: appLang.languageName,
        isRtl: appLang.isRtl,
        sortOrder: appLang.sortOrder,
        isActive: true,
      }));
    }
    return globalLanguages;
  }, [selectedAppType, applicationLanguages, globalLanguages]);

  // Sorted languages for column visibility toggle (English first)
  const sortedLanguages = useMemo(() => {
    return [...languages].sort((a, b) => {
      if (a.languageCode === 'en') return -1;
      if (b.languageCode === 'en') return 1;
      return a.sortOrder - b.sortOrder;
    });
  }, [languages]);

  const isDescriptionHidden = hiddenColumns.has('description');

  const handleHideAllColumns = useCallback(() => {
    // Hide all except English (en) - Key Name is always shown
    const allColumnIds = ['description', ...languages.filter(l => l.languageCode !== 'en').map(l => l.languageCode)];
    setHiddenColumns(new Set(allColumnIds));
    localStorage.setItem('translation-grid-hidden-columns', JSON.stringify(allColumnIds));
  }, [languages]);

  // Fetch translations when app type or page changes (initial load)
  useEffect(() => {
    if (selectedAppType) {
      fetchTranslations();
    }
  }, [fetchTranslations, selectedAppType, currentPage]);

  // Trigger background load after initial fetch completes
  useEffect(() => {
    if (selectedAppType && !isLoading && !isBackgroundLoading && !isAllDataLoaded) {
      fetchAllTranslations();
    }
  }, [selectedAppType, isLoading, isBackgroundLoading, isAllDataLoaded, fetchAllTranslations]);

  const handleAppTypeChange = useCallback((appType: ApplicationType | null) => {
    setSelectedAppType(appType);
  }, [setSelectedAppType]);

  const handleSearchChange = useCallback((query: string) => {
    // Use local search if data is loaded, otherwise fall back to server.
    // Read the flag at call time - a debounced call may have been queued before the cache loaded.
    if (useTranslationStore.getState().isAllDataLoaded) {
      searchLocally(query);
    } else {
      // Server search: setSearchQuery resets the page to 0. If it was already 0 the page
      // effect won't re-run, so fetch here; otherwise the effect does it (avoids a duplicate request).
      const { currentPage: pageBefore, setSearchQuery } = useTranslationStore.getState();
      setSearchQuery(query);
      if (pageBefore === 0) fetchTranslations();
    }
  }, [searchLocally, fetchTranslations]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, [setCurrentPage]);

  const handleAddClick = useCallback(() => {
    setSelectedTranslation(null);
    setIsBladeOpen(true);
  }, []);

  const handleEditClick = useCallback((translation: TranslationKey) => {
    setSelectedTranslation(translation);
    setIsBladeOpen(true);
  }, []);

  const handleBladeClose = useCallback(() => {
    setIsBladeOpen(false);
    setSelectedTranslation(null);
  }, []);

  const handleRetry = useCallback(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  const handleDelete = useCallback(async (translationId: number) => {
    try {
      await deleteTranslation(translationId);
      toast.success('Translation deleted successfully');
    } catch {
      toast.error('Failed to delete translation');
    }
  }, [deleteTranslation]);

  const handleUpdateCell = useCallback(async (translationId: number, languageCode: string, value: string) => {
    const translation = translations.find(t => t.translationKeyId === translationId);
    if (!translation) return;

    // Throwing keeps the cell in edit mode so the user can cancel
    if (languageCode.toLowerCase() !== 'en' && !hasEnglishValue(translation)) {
      toast.error(MISSING_ENGLISH_MESSAGE);
      throw new Error(MISSING_ENGLISH_MESSAGE);
    }

    try {
      await updateTranslation(translationId, buildFullUpdate(translation, { value: { languageCode, value } }));
      toast.success('Translation updated');
    } catch {
      toast.error('Failed to update translation');
      throw new Error('Failed to update');
    }
  }, [translations, updateTranslation]);

  const handleUpdateDescription = useCallback(async (translationId: number, description: string) => {
    const translation = translations.find(t => t.translationKeyId === translationId);
    if (!translation) return;

    if (!hasEnglishValue(translation)) {
      toast.error(MISSING_ENGLISH_MESSAGE);
      throw new Error(MISSING_ENGLISH_MESSAGE);
    }

    try {
      await updateTranslation(translationId, buildFullUpdate(translation, { description }));
      toast.success('Description updated');
    } catch {
      toast.error('Failed to update description');
      throw new Error('Failed to update');
    }
  }, [translations, updateTranslation]);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden">
      {/* Header & Filters - fixed height */}
      <div className="flex-shrink-0 px-6 py-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Translation Management</h1>
            <p className="text-muted-foreground">
              Manage translations for your applications
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/translations/languages')}>
              <Languages className="h-4 w-4 mr-2" />
              Languages
            </Button>
            {selectedAppType && supportsSounds(selectedAppType) && (
              <Button variant="outline" onClick={() => navigate('/translations/sounds')}>
                <Volume2 className="h-4 w-4 mr-2" />
                Sounds
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/translations/weezmo')}>
              <FileCode className="h-4 w-4 mr-2" />
              Weezmo
            </Button>
            <Button variant="outline" onClick={() => navigate('/translations/versions')}>
              <History className="h-4 w-4 mr-2" />
              Versions
            </Button>
            <Button variant="outline" onClick={() => navigate('/translations/publish')}>
              <Server className="h-4 w-4 mr-2" />
              Publish
            </Button>
            <Button variant="outline" onClick={() => navigate('/translations/import')}>
              <Upload className="h-4 w-4 mr-2" />
              Import/Export
            </Button>
            <Button onClick={handleAddClick}>
              <Plus className="h-4 w-4 mr-2" />
              Add Translation
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <ApplicationTypeSelect
            value={selectedAppType}
            onChange={handleAppTypeChange}
            className="w-[300px]"
            required
          />
          <TranslationSearchBar
            value={searchQuery}
            onChange={handleSearchChange}
            isSearchingServer={isSearchingServer}
            isAllDataLoaded={isAllDataLoaded}
            placeholder={isAllDataLoaded ? "Search all columns..." : "Search by key name..."}
            className="flex-1 max-w-md"
          />

          {/* Column visibility toggle */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Columns className="h-4 w-4" />
                Columns
                {hiddenColumns.size > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-muted rounded-full">
                    {hiddenColumns.size} hidden
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-56">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">Toggle Columns</h4>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={handleShowAllColumns}
                    >
                      All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={handleHideAllColumns}
                    >
                      None
                    </Button>
                  </div>
                </div>
                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                  <label className="flex items-center gap-2 cursor-pointer hover:bg-muted p-1.5 rounded">
                    <Checkbox
                      checked={!hiddenColumns.has('keyName')}
                      onCheckedChange={() => handleToggleColumn('keyName')}
                    />
                    <span className="text-sm">Key Name</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer hover:bg-muted p-1.5 rounded">
                    <Checkbox
                      checked={!isDescriptionHidden}
                      onCheckedChange={() => handleToggleColumn('description')}
                    />
                    <span className="text-sm">Description</span>
                  </label>
                  {sortedLanguages.map((lang) => {
                    const isEnglish = lang.languageCode === 'en';
                    return isEnglish ? (
                      // English - always visible, not togglable
                      <div
                        key={lang.languageCode}
                        className="flex items-center gap-2 p-1.5 rounded opacity-50"
                      >
                        <Checkbox checked disabled />
                        <span className="text-sm">
                          {lang.languageName} ({lang.languageCode.toUpperCase()})
                        </span>
                        <span className="text-xs text-muted-foreground ml-auto">(required)</span>
                      </div>
                    ) : (
                      <label
                        key={lang.languageCode}
                        className="flex items-center gap-2 cursor-pointer hover:bg-muted p-1.5 rounded"
                      >
                        <Checkbox
                          checked={!hiddenColumns.has(lang.languageCode)}
                          onCheckedChange={() => handleToggleColumn(lang.languageCode)}
                        />
                        <span className="text-sm">
                          {lang.languageName} ({lang.languageCode.toUpperCase()})
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Background loading indicator */}
          {isBackgroundLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading all translations...</span>
            </div>
          )}
        </div>
      </div>

      {/* Grid - flex-1 to fill remaining space, overflow-hidden to constrain child */}
      <div className="flex-1 min-h-0 overflow-hidden px-6 pb-4">
        <TranslationGrid
          translations={translations}
          languages={languages}
          isLoading={isLoading}
          error={error}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onEdit={handleEditClick}
          onDelete={handleDelete}
          onUpdateCell={handleUpdateCell}
          onUpdateDescription={handleUpdateDescription}
          onRetry={handleRetry}
          isDeleting={isSubmitting}
          isSearchingServer={isSearchingServer}
          hiddenColumns={hiddenColumns}
        />
      </div>

      {/* Add/Edit Blade */}
      <TranslationBlade
        isOpen={isBladeOpen}
        onClose={handleBladeClose}
        translation={selectedTranslation}
        languages={languages}
        applicationType={selectedAppType}
      />
    </div>
  );
}
